/**
 * PEOC Arrivals vocabulary (W4b — coordination-tab council ruling A.6).
 *
 * The single source for the operator-facing copy of the "PEOC Arrivals" surface: its
 * tab label + surface title, the always-visible framing caption, the receipt action +
 * post-ack state, the arrival type labels, the FN dual-notify badge, the neutral
 * authority-kind chip, and the First-Nation Band-Council-Resolution attribution.
 *
 * DOCTRINE (council A.6/A.8; App A #46 Liaison; PERP §6.8.1.2):
 *   • An arrival is ACKNOWLEDGED — the PEOC RECEIVES + coordinates support; it does NOT
 *     approve, command, adjudicate, or assume authority over a declaring community's
 *     emergency. So the action is **"Acknowledge receipt"** and the post-ack state is
 *     **"Received by PEOC"** — NEVER "Approve"/"Accept"/"Confirm"/"Validate"/"Clear".
 *   • "Received"/"Acknowledged" here is a coordination receipt and is EXPLICITLY NOT
 *     App A #9 Check-in (a resource first reporting to an incident) — no copy glosses it
 *     as "check in", and the ack never touches a resource status.
 *   • "Declared" is FORBIDDEN on this surface (the §2.1 reserved-declare collision): the
 *     PROVINCE receives, it does not "declare". A row is a "Declaration of emergency"
 *     (the EMCPA/PERP statutory NOUN — correct-domain-verbatim, like "Band Council
 *     Resolution / BCR"), never a "Declared" state/affordance.
 *   • FN PARITY (PERP §6.6.4 "shall respond … the same way"): a First-Nation Band Council
 *     declaration is a FULL PEER of a municipal one — same card, same weight, same verb.
 *     The ONLY FN-specific elements are (1) the informational "ISC also notified" chip and
 *     (2) the "Band Council Resolution — <First Nation>" attribution. NO FN-only
 *     verify/approve hop, NO provisional styling, NO ownership affordance.
 *
 * Kept in its own module (no React import) so the pinned vocabulary lock
 * (peocArrivalsVocabulary.lock.test.ts) can scan every operator string without importing
 * the panel component tree. Same idiom as requestsTabVocabulary.ts + incidentVocabulary.ts.
 */
import type { PeocArrivalSourceType } from '@tsr/shared-types';

/** Sub-tab label on the Coordination rail (kept short like the sibling tabs). */
export const PEOC_ARRIVALS_TAB_LABEL = 'Arrivals';

/** The surface title inside the panel. */
export const PEOC_ARRIVALS_SURFACE_TITLE = 'PEOC Arrivals';

/**
 * ALWAYS-VISIBLE framing caption (council A.6 — receipt-not-command). This deliberately
 * NEGATES "approve" / "command" / assume-authority; it is pinned verbatim by the lock
 * test and, being a negation, is the ONE string exempt from the forbidden-affordance scan.
 */
export const PEOC_ARRIVALS_FRAMING_CAPTION =
  'Receipt only — the PEOC is being notified and will coordinate support; it does not approve, command, or assume authority over a declaring community’s emergency.';

/** The receipt action button (records receipt — never approve/command/check-in). */
export const ACKNOWLEDGE_RECEIPT_LABEL = 'Acknowledge receipt';

/** The post-ack state (a receipt, not an adjudication). */
export const RECEIVED_BY_PEOC_LABEL = 'Received by PEOC';

/** The not-yet-received state (never "Awaiting review" / "Pending approval" — those would
 *  read as a pending PROVINCIAL disposition, which the PEOC never performs). */
export const AWAITING_RECEIPT_LABEL = 'Not yet acknowledged';

/** The FN dual-notify BADGE (leg b) — informational; NOT a separate arrival. */
export const ISC_ALSO_NOTIFIED_LABEL = 'ISC also notified';

/** Empty state (no gloss that reads as check-in). */
export const PEOC_ARRIVALS_EMPTY = 'No arrivals yet.';

/** Arrival TYPE labels, keyed by source leg. Both declaration paths share ONE label
 *  (FN parity — a First-Nation declaration is not a lesser class). "Declaration of
 *  emergency" is the EMCPA/PERP statutory noun; "Area Command established" is §4.6 +
 *  App A #2 (the oversight entity is set up — never "declared"/"owned"/"complex"). */
export const ARRIVAL_TYPE_LABEL: Record<PeocArrivalSourceType, string> = {
  declaration: 'Declaration of emergency',
  area_command: 'Area Command established',
};

export function arrivalTypeLabel(sourceType: PeocArrivalSourceType): string {
  return ARRIVAL_TYPE_LABEL[sourceType] ?? 'Arrival';
}

/** Neutral jurisdiction-type chip (symmetric — both declaration paths get one, so it is
 *  parity-preserving). Null (an Area-Command establishment) → no chip. */
export function authorityKindLabel(
  authorityKind: 'municipal' | 'first_nation' | null,
): string | null {
  if (authorityKind === 'municipal') return 'Municipal';
  if (authorityKind === 'first_nation') return 'First Nation';
  return null;
}

/** The First-Nation declaring-authority attribution (council A.6). "Band Council
 *  Resolution / BCR" is correct-domain-verbatim (Indian Act s.2; doctrine-reference §4.3
 *  note). Municipal declarations carry NO BCR attribution (a municipality has none). */
export function bandCouncilResolutionAttribution(firstNationName: string): string {
  return `Band Council Resolution — ${firstNationName}`;
}

/** Humanized hazard chip for an arrival (the incident/declaration hazard enum — a
 *  DIFFERENT set from the AnomalyQueue's QueueHazard, so it has its own map). Never leaks a
 *  raw snake_case value: an unknown value is title-cased on `_`. */
const HAZARD_TYPE_LABEL: Record<string, string> = {
  wildfire: 'Wildfire',
  flood: 'Flood',
  ice_storm: 'Ice storm',
  cap_warning: 'CAP warning',
  multi_hazard: 'Multi-hazard',
  other: 'Other',
};

export function hazardTypeLabel(hazardType: string | null): string | null {
  if (!hazardType) return null;
  return (
    HAZARD_TYPE_LABEL[hazardType] ??
    hazardType
      .split('_')
      .map((w) => (w.length > 0 ? w[0]!.toUpperCase() + w.slice(1) : w))
      .join(' ')
  );
}

/** Receipt tooltip — the receiver + time (records receipt; never an approver). */
export function receivedByTooltip(acknowledgedBy: string | null, acknowledgedAtISO: string): string {
  return acknowledgedBy
    ? `${RECEIVED_BY_PEOC_LABEL} · ${acknowledgedBy} · ${acknowledgedAtISO}`
    : `${RECEIVED_BY_PEOC_LABEL} · ${acknowledgedAtISO}`;
}
